// Tracks the Supabase Auth session and resolves the signed-in user's
// household id (looked up from household_accounts -- see
// supabase/migrations/0004_household_scoping_schema.sql). An ordinary signup
// gets exactly one row, via the on_auth_user_created trigger, from the moment
// their account exists; a user who signed up with a pending household invite
// (0041) has NO row until they accept it or start their own household, and
// App.jsx shows a landing screen for that case. householdId is deliberately never persisted
// to localStorage -- it's always re-derived from the live auth state so it
// can't go stale across a sign-out/sign-in swap.
import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";

export function useAuth() {
  // undefined = initial session check still in flight, null = signed out.
  const [session, setSession] = useState(undefined);
  const [householdId, setHouseholdId] = useState(null);
  const [householdLoading, setHouseholdLoading] = useState(false);
  // A failed lookup is NOT the same as "no household": App.jsx offers
  // "Start my own household" for a genuinely empty result, and must never
  // offer it because of a dropped connection. Bumping householdReloadKey
  // re-runs the lookup (retry, or after accepting an invite).
  const [householdError, setHouseholdError] = useState(false);
  const [householdReloadKey, setHouseholdReloadKey] = useState(0);
  // False until a lookup for the current session has finished. Without it the
  // very first render after sign-in (householdId still null, loading not yet
  // flipped by the effect below) would read as "no household".
  const [householdResolved, setHouseholdResolved] = useState(false);

  // Who is signed in, as a plain string. Everything downstream (the household
  // lookup below, MealPlannerProvider, useMealPlannerData) keys on THIS, never
  // on the `session` object: Supabase issues a brand-new session object every
  // time it refreshes the access token (roughly hourly, and on refocus after
  // the token has expired), and keying on the object made each refresh look
  // like a new sign-in -- the household lookup flipped back to "loading", App
  // swapped the whole app for "Setting up your household…", and everything
  // below it unmounted and reloaded. That also cancelled any pending 600ms
  // debounced save (plan defaults, the current week's title/servings/budgets),
  // silently dropping an edit typed just before the refresh.
  const userId = session?.user?.id ?? null;

  useEffect(() => {
    // `cancelled` guards the getSession() promise specifically -- unlike the
    // onAuthStateChange subscription (torn down via unsubscribe below), an
    // in-flight promise from an effect invocation React StrictMode has
    // already cleaned up (it deliberately mounts every effect twice in dev,
    // to surface exactly this kind of bug) keeps running regardless, and
    // would otherwise call setSession from a "dead" invocation.
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSession(data.session ?? null);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (cancelled) return;
      // Supabase silently refreshes the access token whenever the tab
      // regains focus/visibility, firing this callback with a new session
      // object every time even when nothing meaningful changed. Reacting to
      // every one of those (via setSession's new object reference cascading
      // through the householdId-lookup effect below, and from there into
      // useMealPlannerData) was causing the app to redundantly reload and
      // re-save all data on every tab-focus -- wasteful at best, and the
      // trigger for a real data-loss bug when a save and a token refresh
      // overlapped. Identical sessions are dropped here; a session whose
      // token really did change is kept (so `session` never goes stale), but
      // nothing downstream depends on the session object's identity any more
      // -- see `userId` below -- so a genuine token refresh is now harmless.
      setSession((prev) => {
        if (prev?.user?.id === newSession?.user?.id && prev?.access_token === newSession?.access_token) return prev;
        return newSession;
      });
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setHouseholdId(null);
      setHouseholdError(false);
      setHouseholdLoading(false);
      setHouseholdResolved(false);
      return;
    }
    let cancelled = false;
    setHouseholdLoading(true);
    setHouseholdError(false);
    setHouseholdResolved(false);
    supabase
      .from("household_accounts")
      .select("household_id")
      .limit(1)
      .then(({ data, error }) => {
        if (cancelled) return;
        setHouseholdError(!!error);
        setHouseholdId(!error && data && data.length > 0 ? data[0].household_id : null);
        setHouseholdLoading(false);
        setHouseholdResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, householdReloadKey]);

  const reloadHousehold = useCallback(() => setHouseholdReloadKey((k) => k + 1), []);

  // Membership can change while a tab sits open -- the owner removes you, or
  // you accept an invite in another tab. The lookup above no longer re-runs on
  // token refresh (see `userId`), so re-check whenever the tab becomes visible
  // again; if the household this tab is showing is no longer yours (or you now
  // have one and this tab thinks you don't), reload it. A failed check is
  // ignored -- it must never bounce anyone out over a flaky connection.
  useEffect(() => {
    if (!userId || !householdResolved || householdError) return;
    let cancelled = false;
    async function recheck() {
      if (document.visibilityState !== "visible") return;
      const { data, error } = await supabase.from("household_accounts").select("household_id");
      if (cancelled || error || !data) return;
      const stillValid = householdId ? data.some((r) => r.household_id === householdId) : data.length === 0;
      if (!stillValid) setHouseholdReloadKey((k) => k + 1);
    }
    document.addEventListener("visibilitychange", recheck);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", recheck);
    };
  }, [userId, householdId, householdResolved, householdError]);

  // emailRedirectTo is wherever the app is currently running, so this works
  // unchanged in local dev and after a future production deploy -- but the
  // exact origin still has to be added to Supabase's Auth > URL
  // Configuration > Redirect URLs allow-list once, or the dashboard will
  // reject/strip the redirect regardless of what's passed here.
  //
  // Deliberately leaves shouldCreateUser at its default (true): the
  // beta_allowlist "Before User Created" hook already blocks account
  // creation -- and therefore any email send -- for anyone not on the list,
  // before any row is inserted. Setting shouldCreateUser: false here doesn't
  // add protection on top of that; it only breaks first-time sign-in for
  // allowlisted people, since Supabase then refuses ("Signups not allowed
  // for otp") before ever consulting the hook.
  const signInWithOtp = useCallback((email) => supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } }), []);
  const signOut = useCallback(() => supabase.auth.signOut(), []);

  return {
    authLoading: session === undefined,
    session,
    userId,
    householdId,
    householdLoading,
    householdResolved,
    householdError,
    reloadHousehold,
    signInWithOtp,
    signOut,
  };
}
